import { useState } from 'react'
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns'
import Alert from "@mui/material/Alert"
import AlertTitle from "@mui/material/AlertTitle"
import Button from "@mui/material/Button"
import Checkbox from "@mui/material/Checkbox"
import Chip from "@mui/material/Chip"
import CircularProgress from "@mui/material/CircularProgress"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogContentText from "@mui/material/DialogContentText"
import DialogTitle from "@mui/material/DialogTitle"
import IconButton from "@mui/material/IconButton"
import Menu from "@mui/material/Menu"
import Stack from "@mui/material/Stack"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TablePagination from "@mui/material/TablePagination"
import TableRow from "@mui/material/TableRow"
import Tooltip from "@mui/material/Tooltip"
import Typography from "@mui/material/Typography"
import ListItemButton from "@mui/material/ListItemButton"
import Divider from "@mui/material/Divider"
import EditIcon from "@mui/icons-material/Edit"
import DeleteIcon from "@mui/icons-material/Delete"
import FilterListIcon from "@mui/icons-material/FilterList"
import TableHeader from './sections/table-header'
import DialogForm from './sections/dialog-form'
import { tableDataHead } from '@/utils/stub'
import {
  useDeleteIncidentMutation,
  useGetAllIncidentsQuery,
  useGetStatusesQuery,
} from './incidents-api'
import { useSelector } from 'react-redux';
import { selectActiveBuildingId } from '@/features/building/buildingSlice';
import WidgetPaper from '@/components/WidgetPaper'
import SearchInput from '@/components/SearchInput'
import AddButton from '../access/AddButton'

const Incidents = ({ref = null}) => {
  const { t } = useTranslation()
  const activeBuildingId = useSelector(selectActiveBuildingId)
  const { data, isSuccess, isLoading, isError } =
    useGetAllIncidentsQuery(activeBuildingId)
  const { data: statusesData, isSuccess: isStatusesSuccess } = useGetStatusesQuery()

  const [currentId, setCurrentId] = useState(undefined)
  const [handleDelete] = useDeleteIncidentMutation()
  const [page, setPage] = useState(0)
  const [orderBy, setOrderBy] = useState('number')
  const [order, setOrder] = useState('asc')
  const [selected, setSelected] = useState([])
  const [rowsPerPage, setRowsPerPage] = useState(10)
  const [openModal, setOpenModal] = useState(false)
  const [deleteModal, setDeleteModal] = useState(false)
  const [filterId, setFilterId] = useState()
  const [search, setSearch] = useState('')
  const [filterAnchorEl, setFilterAnchorEl] = useState(null)
  const [selectedStatus, setSelectedStatus] = useState(null)

  const incident = data?.find((item) => item.id === currentId)
  const incidents = data
    ?.filter((item) => item.theme.search(search) !== -1 || item?.id === +search)
    .filter((item) => (filterId ? item.status_id === filterId : item))
    ?? []

  const handleFilter = (id) => () => {
    if (selectedStatus === id) {
      setFilterId()
      setSelectedStatus()
    } else {
      setFilterId(id)
      setSelectedStatus(id)
    }
  }

  const onDelete = (id) => () => {
    if (!currentId) {
      setCurrentId(id)
      setDeleteModal(true)
    } else {
      setDeleteModal(false)
      handleDelete(currentId)
    }
  }

  const cancelDelete = () => {
    setDeleteModal(false)
    setCurrentId()
  }

  const handleOpenModal = () => {
    setOpenModal(true)
  }

  const handleCloseModal = () => {
    setOpenModal(false)
    setCurrentId(0)
  }

  const handleEdit = (id) => () => {
    setCurrentId(id)
    handleOpenModal()
  }

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc'
    setOrder(isAsc ? 'desc' : 'asc')
    setOrderBy(property)
  }

  const handleSelectAllClick = (event) => {
    if (event.target.checked) {
      const newSelecteds = data.map((item) => item.id)
      setSelected(newSelecteds)
      return
    }
    setSelected([])
  }

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleChangePage = (event, newPage) => {
    setPage(newPage)
  }

  function descendingComparator(a, b, orderBy) {
    if (b[orderBy] < a[orderBy]) {
      return -1
    }
    if (b[orderBy] > a[orderBy]) {
      return 1
    }
    return 0
  }

  function getComparator(order, orderBy) {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy)
  }

  function stableSort(array, comparator) {
    const stabilizedThis = array?.map((el, index) => [el, index])
    stabilizedThis?.sort((a, b) => {
      const orderTable = comparator(a[0], b[0])
      if (orderTable !== 0) {
        return orderTable
      }
      return a[1] - b[1]
    })
    return stabilizedThis?.map((el) => el[0])
  }

  const handleClick = (event, name) => {
    const selectedIndex = selected.indexOf(name)
    let newSelected = []
    if (selectedIndex === -1) {
      newSelected = newSelected.concat(selected, name)
    } else if (selectedIndex === 0) {
      newSelected = newSelected.concat(selected.slice(1))
    } else if (selectedIndex === selected.length - 1) {
      newSelected = newSelected.concat(selected.slice(0, -1))
    } else if (selectedIndex > 0) {
      newSelected = newSelected.concat(
        selected.slice(0, selectedIndex),
        selected.slice(selectedIndex + 1)
      )
    }
    setSelected(newSelected)
  }

  const statuses = [
    {
      id: 1,
      color: 'warning'
    },
    {
      id: 2,
      color: 'info'
    },
    {
      id: 3,
      color: 'success'
    }
  ]

  return (
    <>
      <WidgetPaper
        ref={ref}
        title={t('incidents')}
      >
        {isLoading && <CircularProgress />}
        <Stack
          direction="row"
          spacing={1}
          sx={{
            alignItems: "center",
            flex: 1,
            minWidth: 0,
            mb: 2
          }}>
          <SearchInput
            placeholder={t('enter_number')}
            onChange={(value) => setSearch(value)}
            delay={300}
          />
          <Tooltip title={t('filter')}>
            <IconButton
              onClick={(event) => setFilterAnchorEl(event.currentTarget)}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.secondary'
              }}
            >
              <FilterListIcon />
            </IconButton>
          </Tooltip>
          <Divider orientation="vertical" flexItem />
          <AddButton onClick={handleOpenModal} />
        </Stack>
        <Menu
          anchorEl={filterAnchorEl}
          open={Boolean(filterAnchorEl)}
          onClose={() => setFilterAnchorEl(null)}
          onClick={() => setFilterAnchorEl(null)}
        >
          {isStatusesSuccess &&
            statusesData.map((status) => (
              <ListItemButton
                key={status.id}
                selected={selectedStatus === status.id}
                onClick={handleFilter(status.id)}
                value={status.id}
              >
                {status.title}
              </ListItemButton>
            ))}
        </Menu>
        {isSuccess && data?.length && (
          <>
            <TableContainer>
              <Table>
                <TableHeader
                  order={order}
                  orderBy={orderBy}
                  headLabel={tableDataHead}
                  rowCount={data?.length}
                  numSelected={selected.length}
                  onRequestSort={handleRequestSort}
                  onSelectAllClick={handleSelectAllClick}
                />
                <TableBody>
                  {stableSort(incidents, getComparator(order, orderBy))
                    ?.slice(
                      page * rowsPerPage,
                      page * rowsPerPage + rowsPerPage
                    )
                    ?.map((table, index) => {
                      const isItemSelected =
                        selected.indexOf(table?.id) !== -1
                      const labelId = `enhanced-table-checkbox-${index}`

                      return (
                        <TableRow
                          hover
                          key={table?.id}
                          tabIndex={-1}
                          role="checkbox"
                          selected={isItemSelected}
                          aria-checked={isItemSelected}
                          onClick={(event) => handleClick(event, table.id)}
                        >
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={isItemSelected}
                              slotProps={{
                                input: {
                                  'aria-labelledby': labelId
                                }
                              }}
                            />
                          </TableCell>
                          <TableCell
                            component="th"
                            scope="row"
                            padding="none"
                            id={labelId}
                          >
                            <Typography variant="subtitle2" noWrap>
                              {table?.id}
                            </Typography>
                          </TableCell>
                          <TableCell align="left">
                            {format(
                              new Date(table?.created_at),
                              'dd.MM.yyyy'
                            )}
                          </TableCell>
                          <TableCell align="left">{`${table?.username}`}</TableCell>
                          <TableCell align="left">{table?.theme}</TableCell>
                          <TableCell align="left">
                            {format(
                              new Date(table?.updated_at),
                              'dd.MM.yyyy'
                            )}
                          </TableCell>
                          <TableCell align="left">{`${table?.operator_username}`}</TableCell>
                          <TableCell align="left">
                            {table.status.title ? (
                              <Chip
                                label={table?.status.title}
                                color={statuses[table.status.id - 1]?.color}
                              />
                            ) : (
                              t('no_status')
                            )}
                          </TableCell>
                          <TableCell>
                            <Stack sx={{
                              flexDirection: "row"
                            }}>
                              <Tooltip title={t('edit')}>
                                <IconButton
                                  size="small"
                                  aria-label="edit"
                                  color="secondary"
                                  sx={{ mr: 2 }}
                                  onClick={handleEdit(table?.id)}
                                >
                                  <EditIcon />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title={t('delete')}>
                                <IconButton
                                  size="small"
                                  aria-label="edit"
                                  color="error"
                                  onClick={onDelete(table?.id)}
                                >
                                  <DeleteIcon />
                                </IconButton>
                              </Tooltip>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              rowsPerPageOptions={[ 10, 25 ]}
              component="div"
              count={data?.length || 0}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
        {!data?.length && isSuccess && (
          <Alert severity="warning" sx={{ mt: 3 }}>
            <AlertTitle>{t('alert')}</AlertTitle>
            {t('list_empty')}
          </Alert>
        )}
        {isError && (
          <Alert severity="error" sx={{ mt: 3 }}>
            <AlertTitle>{t('alert')}</AlertTitle>
            {t('error_occurred')}
          </Alert>
        )}
      </WidgetPaper>
      <Dialog open={deleteModal} fullWidth>
        <DialogTitle>{t('deletion', { theme: incident?.theme })}</DialogTitle>
        <DialogContent>
          <DialogContentText>{t('sure_to_delete_item')}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelDelete}>{t('cancel')}</Button>
          <Button color="error" onClick={onDelete(currentId)}>
            {t('delete')}
          </Button>
        </DialogActions>
      </Dialog>
      <DialogForm
        open={openModal}
        onClose={handleCloseModal}
        buildingId={activeBuildingId}
        currentId={currentId}
      />
    </>
  );
};

export default Incidents
